import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import { renderChildComponent } from "./router.types";
import { StudyScreeningType } from "@/store/modules/screening/screening.types";
import {
  APPRAISALS, AWAITING_SCREENING, CONFLICTED, EXCLUDED,
  EXTRACTIONS, FAILED_IMPORTS, FULL_TEXT_SCREENING, INCLUDED,
  META_ANALYSIS, MY_DECISIONS, OVERVIEW, POSSIBLE_DUPLICATE_RECORDS, PROJECTS, PROTOCOL,
  QUALITATIVE, REMOVED_DUPLICATE_PAGE, RESOLVED, REVIEW, SCREENING, SINGULAR, STUDIES,
  SUMMARY, SYNTHESIS, TITLE_AND_ABSTRACT_SCREENING, TOTAL_RECORDS_IMPORTED
} from "./route-path.types";
import { enforceProjectLicenseAccess } from "./project-license.guard";

Vue.use(VueRouter);

const HomeView = () => import('../views/HomeView.vue');
const ProjectView = () => import('../views/SumariRedirect.vue');
const OverviewView = () => import('../views/SumariRedirect.vue');
const ProtocolView = () => import('../views/SumariRedirect.vue');
const OverviewSummaryView = () => import('../views/SumariRedirect.vue');
const ScreeningView = () => import('../views/Screening/Screening.vue');
const FailedImportsView = () => import('../views/Screening/FailedImports.vue');
const TotalRecordsImportedView = () => import('../views/Screening/TotalRecordsImported.vue');
const RemovedDuplicateRecordsView = () => import('../views/Screening/ExactDuplicateRecords.vue');
const PossibleDuplicateRecordsView = () => import('../views/Screening/PossibleDuplicateRecords.vue');
const TitleAndAbstractScreeningView = () => import('../views/Screening/TitleAndAbstractScreeningView.vue');
const AwaitingScreeningView = () => import('../views/Screening/AwaitingScreeningView.vue');
const IncludedView = () => import('../views/Screening/IncludedScreeningView.vue');
const ExcludedView = () => import('../views/Screening/ExcludedScreeningView.vue');
const ResolvedView = () => import('../views/Screening/ResolvedScreeningView.vue');
const ConflictedView = () => import('../views/Screening/ConflictedScreeningView.vue');
const MyDecisionView = () => import('../views/Screening/MyDecisionScreeningView.vue');
const FullTextScreeningView = () => import('../views/Screening/FullTextScreeningView.vue');
const AppraisalsView = () => import('../views/SumariRedirect.vue');
const ExtractionsView = () => import('../views/SumariRedirect.vue');
const SynthesisView = () => import('../views/SumariRedirect.vue');
const ReviewView = () => import('../views/SumariRedirect.vue');
const PageNotFoundView = () => import('../views/PageNotFound.vue');

/**
 *
 * @param prefix parent path string
 * @param routes childs router configs
 * @returns RouteConfig[]
 * There is no function similar to `pathMatch: full` for matching exact path in vue2 router,
 * so the only way is to manually prefix the child route with parent route.
 * more info: https://github.com/vuejs/vue-router/issues/745
 */
const withPrefix = (prefix: string, routes: RouteConfig[]) => routes.map((route) => {
  if (route.redirect) {
    route.redirect = prefix + route.redirect;
  }
  route.path = prefix + route.path;
  return route;
});

const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
  },
  {
    path: PROJECTS,
    name: 'projects',
    component: renderChildComponent,
    children: withPrefix('/projects/:projectId', [
      {
        path: OVERVIEW,
        name: 'projects.overview',
        component: renderChildComponent,
        props: true,
        children: withPrefix('/projects/:projectId/overview', [
          {
            path: SUMMARY,
            name: 'projects.overview.summary',
            component: OverviewSummaryView,
            props: true
          },
          {
            path: '',
            redirect: SUMMARY
          }
        ])
      },
      {
        path: PROTOCOL,
        name: 'projects.protocol',
        component: ProtocolView,
        props: true
      },
      {
        path: SCREENING,
        name: 'projects.screening',
        component: ScreeningView,
        props: true
      },
      {
        path: SCREENING + FAILED_IMPORTS,
        name: 'projects.screening.failed-imports',
        component: FailedImportsView,
        meta: { hideBreadcrumb: true },
        props: true
      },
      {
        path: SCREENING + TOTAL_RECORDS_IMPORTED,
        name: 'projects.screening.total-records-imported',
        component: TotalRecordsImportedView,
        meta: { hideBreadcrumb: true },
        props: true
      },
      {
        path: SCREENING + REMOVED_DUPLICATE_PAGE,
        name: 'projects.screening.removed-duplicate-records',
        component: RemovedDuplicateRecordsView,
        meta: { hideBreadcrumb: true },
        props: true
      },
      {
        path: SCREENING + POSSIBLE_DUPLICATE_RECORDS,
        name: 'projects.screening.possible-duplicate-records',
        component: PossibleDuplicateRecordsView,
        meta: { hideBreadcrumb: true },
        props: true
      },
      {
        path: SCREENING + TITLE_AND_ABSTRACT_SCREENING,
        name: 'projects.screening.title-and-abstract-screening',
        component: TitleAndAbstractScreeningView,
        props: (route) => ({
          page: route.query.page,
          perPage: route.query.perPage,
          searchInput: route.query.searchInput,
          sortBy: route.query.sortBy,
          sortOrder: route.query.sortOrder
        }),
        children: withPrefix('/projects/:projectId/screening/title-and-abstract-screening', [
          {
            path: AWAITING_SCREENING,
            name: 'projects.screening.title-and-abstract-screening.awaiting-screening',
            component: AwaitingScreeningView,
            props: (route) => ({
              screeningType: StudyScreeningType.TITLE_ABSTRACT,
              page: route.query.page,
              perPage: route.query.perPage,
              searchInput: route.query.searchInput,
              sortBy: route.query.sortBy,
              sortOrder: route.query.sortOrder
            }),
          },
          {
            path: INCLUDED,
            name: 'projects.screening.title-and-abstract-screening.included',
            component: IncludedView,
            props: (route) => ({
              screeningType: StudyScreeningType.TITLE_ABSTRACT,
              page: route.query.page,
              perPage: route.query.perPage,
              searchInput: route.query.searchInput,
              sortBy: route.query.sortBy,
              sortOrder: route.query.sortOrder
            }),
          },
          {
            path: EXCLUDED,
            name: 'projects.screening.title-and-abstract-screening.excluded',
            component: ExcludedView,
            props: (route) => ({
              screeningType: StudyScreeningType.TITLE_ABSTRACT,
              page: route.query.page,
              perPage: route.query.perPage,
              searchInput: route.query.searchInput,
              sortBy: route.query.sortBy,
              sortOrder: route.query.sortOrder
            }),
          },
          {
            path: RESOLVED,
            name: 'projects.screening.title-and-abstract-screening.resolved',
            component: ResolvedView,
            props: (route) => ({
              screeningType: StudyScreeningType.TITLE_ABSTRACT,
              page: route.query.page,
              perPage: route.query.perPage,
              searchInput: route.query.searchInput,
              sortBy: route.query.sortBy,
              sortOrder: route.query.sortOrder
            }),
          },
          {
            path: CONFLICTED,
            name: 'projects.screening.title-and-abstract-screening.conflicted',
            component: ConflictedView,
            props: (route) => ({
              screeningType: StudyScreeningType.TITLE_ABSTRACT,
              page: route.query.page,
              perPage: route.query.perPage,
              searchInput: route.query.searchInput,
              sortBy: route.query.sortBy,
              sortOrder: route.query.sortOrder
            }),
          },
          {
            path: MY_DECISIONS,
            name: 'projects.screening.title-and-abstract-screening.my-decisions',
            component: MyDecisionView,
            props: (route) => ({
              screeningType: StudyScreeningType.TITLE_ABSTRACT,
              page: route.query.page,
              perPage: route.query.perPage,
              searchInput: route.query.searchInput,
              sortBy: route.query.sortBy,
              sortOrder: route.query.sortOrder
            }),
          },
          {
            path: '',
            redirect: AWAITING_SCREENING
          }
        ])
      },
      {
        path: SCREENING + FULL_TEXT_SCREENING,
        name: 'projects.screening.full-text-screening',
        component: FullTextScreeningView,
        props: (route) => ({
          page: route.query.page,
          perPage: route.query.perPage,
          searchInput: route.query.searchInput,
          sortBy: route.query.sortBy,
          sortOrder: route.query.sortOrder
        }),
        children: withPrefix('/projects/:projectId/screening/full-text-screening', [
          {
            path: AWAITING_SCREENING,
            name: 'projects.screening.full-text-screening.awaiting-screening',
            component: AwaitingScreeningView,
            props: (route) => ({
              screeningType: StudyScreeningType.FULL_TEXT,
              page: route.query.page,
              perPage: route.query.perPage,
              searchInput: route.query.searchInput,
              sortBy: route.query.sortBy,
              sortOrder: route.query.sortOrder
            }),
          },
          {
            path: INCLUDED,
            name: 'projects.screening.full-text-screening.included',
            component: IncludedView,
            props: (route) => ({
              screeningType: StudyScreeningType.FULL_TEXT,
              page: route.query.page,
              perPage: route.query.perPage,
              searchInput: route.query.searchInput,
              sortBy: route.query.sortBy,
              sortOrder: route.query.sortOrder
            }),
          },
          {
            path: EXCLUDED,
            name: 'projects.screening.full-text-screening.excluded',
            component: ExcludedView,
            props: (route) => ({
              screeningType: StudyScreeningType.FULL_TEXT,
              page: route.query.page,
              perPage: route.query.perPage,
              searchInput: route.query.searchInput,
              sortBy: route.query.sortBy,
              sortOrder: route.query.sortOrder
            }),
          },
          {
            path: RESOLVED,
            name: 'projects.screening.full-text-screening.resolved',
            component: ResolvedView,
            props: (route) => ({
              screeningType: StudyScreeningType.FULL_TEXT,
              page: route.query.page,
              perPage: route.query.perPage,
              searchInput: route.query.searchInput,
              sortBy: route.query.sortBy,
              sortOrder: route.query.sortOrder
            }),
          },
          {
            path: CONFLICTED,
            name: 'projects.screening.full-text-screening.conflicted',
            component: ConflictedView,
            props: (route) => ({
              screeningType: StudyScreeningType.FULL_TEXT,
              page: route.query.page,
              perPage: route.query.perPage,
              searchInput: route.query.searchInput,
              sortBy: route.query.sortBy,
              sortOrder: route.query.sortOrder
            }),
          },
          {
            path: MY_DECISIONS,
            name: 'projects.screening.full-text-screening.my-decisions',
            component: MyDecisionView,
            props: (route) => ({
              screeningType: StudyScreeningType.FULL_TEXT,
              page: route.query.page,
              perPage: route.query.perPage,
              searchInput: route.query.searchInput,
              sortBy: route.query.sortBy,
              sortOrder: route.query.sortOrder
            }),
          },
          {
            path: '',
            redirect: AWAITING_SCREENING
          }
        ])
      },
      {
        path: APPRAISALS,
        name: 'projects.appraisals',
        component: AppraisalsView,
        props: true
      },
      {
        path: EXTRACTIONS,
        name: 'projects.extractions',
        component: ExtractionsView,
        props: true
      },
      {
        path: SYNTHESIS + QUALITATIVE + STUDIES,
        name: 'projects.synthesis',
        component: SynthesisView,
        props: true
      },
      {
        path: SYNTHESIS + META_ANALYSIS,
        name: 'projects.synthesis.metaAnalysis',
        component: SynthesisView,
        props: true
      },
      {
        path: SYNTHESIS + META_ANALYSIS + SINGULAR,
        name: 'projects.synthesis.metaAnalysis.singular',
        component: SynthesisView,
        props: true
      },
      {
        path: REVIEW,
        name: 'projects.review',
        component: ReviewView,
        props: true
      },
      {
        path: '',
        redirect: '/screening'
      }
    ]),
    props: true // Pass projectId to all children
  },
  {
    path: '/projects',
    name: 'sumari.projects',
    component: ProjectView
  },
  {
    path: '/jbi-global',
    name: 'jbi.global',
    beforeEnter() {
      window.open('https://jbi.global/', '_blank');
    }
  },
  {
    path: '*',
    name: 'page-not-found',
    component: PageNotFoundView
  }
];

const router = new VueRouter({
  linkExactActiveClass: 'exact-active',
  routes,
});

router.beforeEach(async (to, _from, next) => {
  const allowed = await enforceProjectLicenseAccess(to);
  if (!allowed) {
    next(false);
    return;
  }
  next();
});

router.afterEach(() => {
  const appLoading = document.getElementById('loading-background');
  if (appLoading) {
    appLoading.style.display = 'none';
  }
});

export default router;
